import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface Reward {
  id: string;
  title: string;
  description: string;
  cost: number;
  category: 'discount' | 'donation' | 'badge' | 'experience';
  image_url: string;
  icon: string;
  available: number;
  active: boolean;
}

const mockRewards: Reward[] = [
  {
    id: 'reward-1',
    title: '20% Off Eco-Friendly Products',
    description: 'Get 20% discount at EcoShop for sustainable products',
    cost: 500,
    category: 'discount',
    image_url: 'https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'ShoppingBag',
    available: 50,
    active: true
  },
  {
    id: 'reward-2',
    title: 'Plant 5 Trees',
    description: 'We\'ll donate to plant 5 trees in your name through our partner organizations',
    cost: 1000,
    category: 'donation',
    image_url: 'https://images.pexels.com/photos/1072824/pexels-photo-1072824.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'TreePine',
    available: 25,
    active: true
  },
  {
    id: 'reward-3',
    title: 'Eco Champion Badge',
    description: 'Exclusive digital badge to showcase your environmental commitment',
    cost: 300,
    category: 'badge',
    image_url: 'https://images.pexels.com/photos/1037999/pexels-photo-1037999.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'Star',
    available: 100,
    active: true
  },
  {
    id: 'reward-4',
    title: 'Clean Energy Donation',
    description: 'Support renewable energy projects with a $10 donation',
    cost: 750,
    category: 'donation',
    image_url: 'https://images.pexels.com/photos/2800832/pexels-photo-2800832.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'Zap',
    available: 30,
    active: true
  },
  {
    id: 'reward-5',
    title: 'Eco Workshop Access',
    description: 'Free access to online sustainability workshops and webinars',
    cost: 400,
    category: 'experience',
    image_url: 'https://images.pexels.com/photos/3184317/pexels-photo-3184317.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'Gift',
    available: 20,
    active: true
  },
  {
    id: 'reward-6',
    title: 'Wildlife Conservation Support',
    description: 'Contribute $15 to wildlife conservation efforts',
    cost: 1200,
    category: 'donation',
    image_url: 'https://images.pexels.com/photos/247431/pexels-photo-247431.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'Heart',
    available: 15,
    active: true
  }
];

export function useRewards() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock rewards data');
        setRewards(mockRewards);
        setLoading(false);
        return;
      }

      console.log('Fetching rewards from database...');

      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching rewards:', error);
        
        // If no rewards exist, create sample rewards
        if (error.code === 'PGRST116' || !data || data.length === 0) {
          console.log('Creating sample rewards...');
          await createSampleRewards();
          // Retry fetching after creating sample data
          const { data: retryData } = await supabase
            .from('rewards')
            .select('*')
            .eq('active', true)
            .order('created_at', { ascending: false });
          
          setRewards(retryData || mockRewards);
          setLoading(false);
          return;
        }
        
        setRewards(mockRewards);
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No rewards found, creating sample rewards...');
        await createSampleRewards();
        // Retry fetching after creating sample data
        const { data: retryData } = await supabase
          .from('rewards')
          .select('*')
          .eq('active', true)
          .order('created_at', { ascending: false });
        
        setRewards(retryData || mockRewards);
        setLoading(false);
        return;
      }

      setRewards(data);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      setRewards(mockRewards);
    } finally {
      setLoading(false);
    }
  };

  const createSampleRewards = async () => {
    const sampleRewards = mockRewards.map(reward => ({
      title: reward.title,
      description: reward.description,
      cost: reward.cost,
      category: reward.category,
      image_url: reward.image_url,
      icon: reward.icon,
      available: reward.available,
      active: true
    }));

    const { error } = await supabase
      .from('rewards')
      .insert(sampleRewards);

    if (error) {
      console.error('Error creating sample rewards:', error);
    } else {
      console.log('Sample rewards created successfully');
    }
  };

  return {
    rewards,
    loading,
    refetch: fetchRewards
  };
}